import { VStack } from "@chakra-ui/react";
import { Form, Formik, FormikConfig } from "formik";
import { FC } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { UserRole } from "~/src/__generated__/schema";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";

import { defaultFormUrlsValue } from "./constants";
import { FormUrlsField } from "./FormUrlsField";
import { UrlRegexField } from "./UrlRegexField";
import { FormValues, validationSchema } from "./validationSchema";

export type Props = {
  userRole: UserRole;
  initialValues?: FormValues;
  onCancelClick: () => void;
  onSubmit: FormikConfig<FormValues>["onSubmit"];
};

const defaultInitialValues = {
  title: "",
  landingUrlForPC: "",
  landingUrlForMobile: "",
  landingUrlForTablet: "",
  landingUrlMatchType: "EQUAL_TYPE",
  landingUrlRegularExpression: "",
  landingQueryMatchType: "EQUAL_MATCH",
  landingUrlFormIncluded: false,
  landingIsEnableWhiteout: false,
  landingIsSpa: false,
  formUrls: [defaultFormUrlsValue],
  confirmUrlForPC: "",
  confirmUrlForMobile: "",
  confirmUrlForTablet: "",
  confirmUrlMatchType: "EQUAL_TYPE",
  confirmUrlRegularExpression: "",
  confirmQueryMatchType: "EQUAL_MATCH",
  confirmUrlFormIncluded: false,
  confirmIsEnableWhiteout: false,
  confirmIsSpa: false,
  thanksUrlForPC: "",
  thanksUrlForMobile: "",
  thanksUrlForTablet: "",
  thanksUrlMatchType: "EQUAL_TYPE",
  thanksUrlRegularExpression: "",
  thanksQueryMatchType: "EQUAL_MATCH",
  thanksUrlFormIncluded: false,
  thanksIsEnableWhiteout: false,
  thanksIsSpa: false,
  isExperiment: false,
};

export const FunnelForm: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({
  userRole,
  initialValues = defaultInitialValues,
  onCancelClick,
  onSubmit,
}) => (
  <Formik<FormValues>
    enableReinitialize
    initialValues={initialValues}
    validationSchema={validationSchema}
    validateOnMount={false}
    validateOnChange={false}
    onSubmit={onSubmit}
  >
    <Form>
      <VStack spacing="24px" alignItems="flex-start">
        <FormControlGroup>
          <FormControl required label="ファネル名">
            <FormikTextField type="text" mode="fill" name="title" />
          </FormControl>
        </FormControlGroup>
        <FormControlGroup description="ランディングページのURLを入力してください。このページを訪問し、サンクスページを訪問することでコンバージョンが計測されます。">
          <FormControl required label="PC用URL">
            <FormikTextField type="text" mode="fill" name="landingUrlForPC" />
          </FormControl>
          <FormControl label="モバイル用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="landingUrlForMobile"
            />
          </FormControl>
          <FormControl label="タブレット用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="landingUrlForTablet"
            />
          </FormControl>
          {(userRole === "ADMIN" ||
            userRole === "SUPER" ||
            userRole === "CLIENT" ||
            userRole === "ADMIN_ALL") && (
            <FormControl label="クエリの判定方法">
              <FormikSelectField
                name="landingQueryMatchType"
                options={GraphQLEnums.QueryMatch}
              />
            </FormControl>
          )}
          <FormControl label="URLの一致タイプ">
            <FormikSelectField
              name="landingUrlMatchType"
              options={GraphQLEnums.UrlMatch}
            />
          </FormControl>
          <UrlRegexField
            urlMatchTypeFieldName="landingUrlMatchType"
            regexFieldName="landingUrlRegularExpression"
          />
          <FormControl label="フォームの有無">
            <FormikSwitchField name="landingUrlFormIncluded" />
          </FormControl>
          <FormControl label="ホワイトアウトを有効にする">
            <FormikSwitchField name="landingIsEnableWhiteout" />
          </FormControl>
          <FormControl label="シングルページアプリケーション">
            <FormikSwitchField name="landingIsSpa" />
          </FormControl>
        </FormControlGroup>
        <FormControlGroup description="入力フォームページのURLを入力してください。">
          <FormUrlsField userRole={userRole} />
        </FormControlGroup>
        <FormControlGroup description="確認ページのURLを入力してください。">
          <FormControl label="PC用URL">
            <FormikTextField type="text" mode="fill" name="confirmUrlForPC" />
          </FormControl>
          <FormControl label="モバイル用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="confirmUrlForMobile"
            />
          </FormControl>
          <FormControl label="タブレット用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="confirmUrlForTablet"
            />
          </FormControl>
          {(userRole === "ADMIN" ||
            userRole === "SUPER" ||
            userRole === "CLIENT" ||
            userRole === "ADMIN_ALL") && (
            <FormControl label="クエリの判定方法">
              <FormikSelectField
                name="confirmQueryMatchType"
                options={GraphQLEnums.QueryMatch}
              />
            </FormControl>
          )}
          <FormControl label="URLの一致タイプ">
            <FormikSelectField
              name="confirmUrlMatchType"
              options={GraphQLEnums.UrlMatch}
            />
          </FormControl>
          <UrlRegexField
            urlMatchTypeFieldName="confirmUrlMatchType"
            regexFieldName="confirmUrlRegularExpression"
          />
          <FormControl label="ホワイトアウトを有効にする">
            <FormikSwitchField name="confirmIsEnableWhiteout" />
          </FormControl>
          <FormControl label="シングルページアプリケーション">
            <FormikSwitchField name="confirmIsSpa" />
          </FormControl>
        </FormControlGroup>
        <FormControlGroup description="サンクスページのURLを入力してください。ランディングページに訪問し、サンクスページに訪問することでコンバージョンが計測されます。">
          <FormControl required label="PC用URL">
            <FormikTextField type="text" mode="fill" name="thanksUrlForPC" />
          </FormControl>
          <FormControl label="モバイル用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="thanksUrlForMobile"
            />
          </FormControl>
          <FormControl label="タブレット用URL">
            <FormikTextField
              type="text"
              mode="fill"
              name="thanksUrlForTablet"
            />
          </FormControl>
          {(userRole === "ADMIN" ||
            userRole === "SUPER" ||
            userRole === "CLIENT" ||
            userRole === "ADMIN_ALL") && (
            <FormControl label="クエリの判定方法">
              <FormikSelectField
                name="thanksQueryMatchType"
                options={GraphQLEnums.QueryMatch}
              />
            </FormControl>
          )}
          <FormControl label="URLの一致タイプ">
            <FormikSelectField
              name="thanksUrlMatchType"
              options={GraphQLEnums.UrlMatch}
            />
          </FormControl>
          <UrlRegexField
            urlMatchTypeFieldName="thanksUrlMatchType"
            regexFieldName="thanksUrlRegularExpression"
          />
          <FormControl label="ホワイトアウトを有効にする">
            <FormikSwitchField name="thanksIsEnableWhiteout" />
          </FormControl>
          <FormControl label="シングルページアプリケーション">
            <FormikSwitchField name="thanksIsSpa" />
          </FormControl>
        </FormControlGroup>
        <FormControlGroup>
          <FormControl label="テスト用のファネル">
            <FormikSwitchField name="isExperiment" />
          </FormControl>
        </FormControlGroup>
        <FormikFormButtons onCancelClick={onCancelClick} />
      </VStack>
    </Form>
  </Formik>
);
